import { useEffect, useState } from 'react';
import {
  Button,
  Drawer,
  Input,
  InputTypes,
  MLPVersion,
  useTranslation,
  ButtonWidth,
  FAIcon,
  Theme,
  useNextgenRouter,
} from '@nextgen-web-framework/all';
import { ConsoleLogger, StorageService, type AppState } from '@nextgen-web-framework/core';
import {
  type Cart,
  CartConstantsEnum,
  type CartEntry,
  type InitialCartState,
  updateCart,
  cartApi,
  CartEventsEnum,
  PublishEvent,
  type CartEntryDetails,
  ApiProjections,
  getAllExceptionWithSeverity,
  ExceptionsEnum,
  Severity,
  OrderType,
  CartEntryRequest,
  validateBundleInEntries,
  createOrFetchCart,
  isGroupOrder,
  SubCartDetail,
  hideBV,
} from '@commerce-cart-webcomponents/sdk';
import { useSelector, useDispatch } from 'react-redux';
import { GCOProductCard } from '@commerce-product-webcomponents/ui';
import { SubCarts } from '@commerce-cart-webcomponents/sdk/services/models/SubCarts';
import { AmwayUserDetails } from '@commerce-cart-webcomponents/sdk/services/models/AmwayUserDetails';
import { IProduct, productApi, ProductData, productDataTransformer } from '@commerce-product-webcomponents/sdk';
import { QtySelectorSubCart } from '../qty-selector-sub-cart/QtySelectorSubCart';
import { ChangeOrderType } from '../../common/components/change-order-type/ChangeOrderType';
import { useCartNotification } from '../../common/hooks/useCartNotification';
import { CartNotificationTheme } from '../../common/components/cart-notification/types/enums';
import { AccountSearchDrawer } from '../../common/components/account-search-drawer/AccountSearchDrawer';
import { TOAST_TIMEOUT } from '../../common/types/enums';
import { RouteType } from '../mini-cart/types/enums';
import { AddToCartDrawerProps } from './types/propsType';
import styles from './AddToCartDrawer.module.scss';

export const AddToCartDrawer = ({
  entryObj,
  isMinProjection = false,
  isPDP = false,
  ...props
}: AddToCartDrawerProps) => {
  const t = useTranslation();
  const dispatch = useDispatch();
  const { createToast, deleteToast } = useCartNotification('addToCartDrawer');
  const [displayCartGCODrawer, setDisplayCartGCODrawer] = useState(false);
  const [searchDrawerDisplay, setSearchDrawerDisplay] = useState(false);
  const [subcartCount, setSubcartCount] = useState<Array<number>>([0]);
  const [disabledConfirmBtn, setDisabledConfirmBtn] = useState<boolean>(true);
  const commerceCartState = useSelector((state: AppState) => state.commerceCart);
  const { cart }: InitialCartState = commerceCartState;
  const [cartData, setCartData] = useState<Cart>({
    ...cart,
    subCarts: [],
  });
  const { title = t('cart.addToCartButton.addToCart') } = props;
  const drawerHeaderTitle = t('cart.addToCartDrawer.drawerHeaderTitle');
  const titleText = t('cart.addToCartDrawer.headerTitle');
  const placeHolderText = t('cart.addToCartDrawer.placeHolderText');
  const accountCancelText = t('cart.addToCartDrawer.accountCancel');
  const accountAddCartText = t('cart.addToCartDrawer.accountAddCart');
  const activeCartText = t('cart.addToCartDrawer.activeCarts');
  const itemAddedTocart = t('cart.cartEllipsesMenu.addedCart');
  const accoutExceptionText = t('cart.addToCartDrawer.accountException');
  const [productData, setProductData] = useState({} as IProduct);
  const [updatedProductId, setUpdatedProductId] = useState<string>(entryObj.productId);
  const router = useNextgenRouter();

  const selectedVariantHandler = (productId: string) => {
    setUpdatedProductId(productId);
  };

  function handleDrawerClose() {
    setDisplayCartGCODrawer(true);
    setSearchDrawerDisplay(false);
  }

  useEffect(() => {
    if (displayCartGCODrawer) {
      productApi
        .getProductByIdOrError({
          productId: entryObj.productId,
          warehouseId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.WarehouseId),
        })
        .then((productData: ProductData | Response) => {
          if (!(productData instanceof Response)) {
            setProductData(productDataTransformer(productData));
          }
        })
        .catch((e) => {
          ConsoleLogger.log(e);
        });
    }
  }, [displayCartGCODrawer]);

  function handleInputClick() {
    const accountException = getAllExceptionWithSeverity(
      [ExceptionsEnum.Exceptions.AccountSearchService],
      Severity.Warn,
      cart.errors ?? [],
    );
    if (accountException?.length) {
      createToast(
        {
          theme: CartNotificationTheme.RED,
          rightSeperator: false,
          content: accoutExceptionText,
          clickIcon: 'times',
          icon: 'exclamation-square',
          timeOut: TOAST_TIMEOUT,
        },
        deleteToast,
      );

      return;
    }
    setDisplayCartGCODrawer(false);
    setSearchDrawerDisplay(true);
  }

  function handleAddCart(profile: AmwayUserDetails) {
    const payload = {
      version: Number(StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartReqVersion)),
      orderType: cart.orderType,
      entries: [],
      amwayUser: {
        amwayUserId: profile?.amwayUserId as string,
        accountId: profile?.accountId,
      },
    };

    cartApi
      .createSubCart({
        cartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
        payload,
      })
      .then((res) => {
        dispatch(updateCart(res as Cart));
        setSearchDrawerDisplay(false);
        setDisplayCartGCODrawer(true);
      });
  }

  const createAddToCartRequestGCO = (cart: Cart) => {
    const version = Number(StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartReqVersion));
    const payload: CartEntryRequest = {
      subCarts: [],
      version,
    };

    for (const subCart of cart.subCarts) {
      const transformedEntries = (subCart.entries ?? []).map((entry) => ({
        product: {
          id: entry.product.id,
        },
        quantity: entry.quantity,
        bundleSlots: entry.bundleSlots,
      }));
      payload.subCarts.push({
        subCartId: subCart.subCartId,
        entries: validateBundleInEntries(transformedEntries),
      });
    }

    return payload;
  };

  const triggerAddToCartevent = (res: Cart) => {
    for (const subCart of res.subCarts) {
      const entry = subCart?.entries?.find((entry) => entryObj?.productId === entry.product.id);
      if (entry) {
        PublishEvent({
          cartEvent: CartEventsEnum.AddToCart,
          cartEntry: entry,
          cartId: res.id,
          cart: res,
        });
      }
    }
  };

  function handleConfirmClick() {
    setSubcartCount(subcartCount.map(() => 0));
    setCartData((prev) => {
      const version = Number(StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartReqVersion));
      const payload = createAddToCartRequestGCO(prev);

      cartApi
        .addToCart({
          cartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
          subCartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
          projections:
            isMinProjection && !router.pathname?.includes(RouteType.CART)
              ? ApiProjections.MIN
              : ApiProjections.DETAILED,
          payload,
        })
        .then((res) => {
          createToast(
            {
              theme: CartNotificationTheme.SUCCESS,
              rightSeperator: false,
              content: itemAddedTocart,
              clickIcon: 'times',
              icon: 'check-circle',
              timeOut: TOAST_TIMEOUT,
            },
            deleteToast,
          );
          // const entry = getProductEntry(res as Cart, entryObj?.productId);

          // PublishEvent({
          //   cartEvent: CartEventsEnum.AddToCart,
          //   cartEntry: entry,
          //   cartId: (res as Cart).id,
          //   cart: res as Cart,
          // });
          triggerAddToCartevent(res as Cart);
          dispatch(updateCart(res as Cart));
          PublishEvent({
            cartEvent: CartEventsEnum.UpdateMiniCartIcon,
            cart: res as Cart,
          });
          setDisplayCartGCODrawer(false);
          setCartData({
            ...cart,
            subCarts: [],
          });
        });

      return {
        ...prev,
        version,
      };
    });
  }

  function handleQtyChange(count: number, cart: SubCartDetail) {
    if (subcartCount.every((element) => element === 0)) {
      setDisabledConfirmBtn(true);
    } else {
      setDisabledConfirmBtn(false);
    }

    const updatedCart: Cart = { ...cartData };
    const data: CartEntryDetails = {
      bundleSlots: entryObj.bundleSlots,
      product: {
        id: updatedProductId,
      },
      quantity: count,
    };
    const findSubCartExist = updatedCart.subCarts?.findIndex((subCart) => subCart.subCartId === cart.subCartId);

    if (findSubCartExist === -1) {
      updatedCart.subCarts?.push({
        subCartId: cart.subCartId,
        entries: [data],
        amwayUser: cart.amwayUser,
      });
    } else {
      ((updatedCart.subCarts as SubCarts[])[findSubCartExist as number].entries as CartEntry[])[0].quantity = count;
    }
    setCartData(updatedCart);
  }

  const handleAddToCartClick = async () => {
    const cartIdExistCheck = StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId);
    if (cartIdExistCheck) {
      setDisplayCartGCODrawer(true);
      return;
    }
    const fetchedCartOrNewCart = await createOrFetchCart();
    //Remove api call. Based on backend
    if (!isGroupOrder(fetchedCartOrNewCart.orderType)) {
      await cartApi.updateGCOFlag({
        cartId: fetchedCartOrNewCart.id,
        payload: {
          orderType: OrderType.Gco,
          version: fetchedCartOrNewCart.version,
        },
      });
    }
    dispatch(updateCart({ ...fetchedCartOrNewCart, orderType: OrderType.Gco }));

    setDisplayCartGCODrawer(true);
  };

  const checkEmptySubCarts = () => {
    let result = true;
    cartData.subCarts?.forEach((subCart) => {
      if (subCart.entries?.length) {
        result = false;
      }
    });

    return result;
  };

  return (
    <>
      <Button buttonWidth={ButtonWidth.FULL_WIDTH} title={title} onClick={handleAddToCartClick} {...props} />
      {displayCartGCODrawer && (
        <Drawer
          closeIcon={true}
          bottomGap={0}
          onClose={() => {
            setSubcartCount(subcartCount.map(() => 0));
            setDisplayCartGCODrawer(false);
          }}
          footer={
            <div className={styles['first-drawer-footer']}>
              <Button
                disabled={checkEmptySubCarts() || disabledConfirmBtn}
                onClick={handleConfirmClick}
                buttonWidth={ButtonWidth.FULL_WIDTH}
                title={t('cart.DrawerForm.confirmButtonModalTitle')}
                data-testId="confirm-button"
              />
            </div>
          }
          header={<div className={styles['drawer-header-title']}>{drawerHeaderTitle}</div>}
          useReactPortal={true}
          portalContainerId={'react-portal-drawer'}
        >
          <div className={styles['add-to-cart-drawer-container']}>
            <ChangeOrderType
              onSuccessCB={() => setDisplayCartGCODrawer(false)}
              productDetails={[{ ...entryObj }]}
              cartData={cart}
            />
            <div className={styles['GCO-product-card-container']}>
              <GCOProductCard
                link={entryObj.link as string}
                alt={entryObj.alt || ''}
                brandName={entryObj.brandName || ''}
                productName={entryObj.productName || ''}
                productId={entryObj.productId}
                productSize={entryObj.productSize || ''}
                productPrice={entryObj.productPrice}
                pointValue={entryObj.pointValue || ''}
                businessVolume={(!hideBV() && entryObj.businessVolume) || ''}
                variantGroupData={isPDP ? undefined : (productData.variantData ?? entryObj.variantGroupData)}
                selectedVariantCb={selectedVariantHandler}
              />
            </div>
            <div className={styles['new-cart-text-container']}>
              <div className={styles['new-cart-text-container-title']}>{titleText}</div>
              <div className={styles['new-cart-text-container-input']}>
                <div className={styles['account-name-search']}>
                  <Input
                    type={InputTypes.TEXT}
                    label={placeHolderText}
                    mlpVersion={MLPVersion.TWO}
                    onClick={handleInputClick}
                    name="new-cart-name"
                  />
                  <FAIcon
                    iconName="search"
                    theme={Theme.BLACK}
                    className={styles['account-name-search-icon']}
                    onClick={handleInputClick}
                  />
                </div>
              </div>
            </div>

            <div className={styles['add-to-cart-drawer-container-active-carts']}>
              <div className={styles['add-to-cart-drawer-container-active-carts-title']}>{activeCartText}</div>
              <div className={styles['add-to-cart-drawer-container-active-carts-list']}>
                {(cart.subCarts ? cart.subCarts : []).map((cart, i) => (
                  <QtySelectorSubCart
                    key={cart.subCartId}
                    enableHoverEffect={false}
                    disableMinusButton={subcartCount[i] === 0}
                    onQuantityChange={(count) => {
                      subcartCount[i] = count;
                      handleQtyChange(count, cart);
                    }}
                    initialCount={subcartCount[i]}
                    accountName={
                      i === 0
                        ? `${cart.amwayUser?.primaryName?.split(' ')[0] ?? ''} (${t('cart.gcoList.You')})`
                        : (cart.amwayUser?.primaryName ?? '')
                    }
                    accontNumber={i === 0 ? 0 : Number(cart.amwayUser?.accountId)}
                  />
                ))}
              </div>
            </div>
          </div>
        </Drawer>
      )}
      {searchDrawerDisplay && (
        <AccountSearchDrawer
          cartData={cart}
          onPrimaryButtonClick={handleDrawerClose}
          drawerHeaderTitle={drawerHeaderTitle}
          placeHolderText={placeHolderText}
          primaryButtonText={accountCancelText}
          secondaryButtonText={accountAddCartText}
          onRadioChange={handleAddCart}
        />
      )}
    </>
  );
};

export type { AddToCartDrawerProps };
